import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { createMyApiKey } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

type Props = {
  /** invoked with the plaintext key on success — caller opens RevealKeyModal */
  onCreated: (apiKey: string) => void;
};

export function CreateKeyModal({ onCreated }: Props) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => createMyApiKey(label.trim()),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ["me", "api-keys"] });
      onCreated(res.key);
      setLabel("");
      setOpen(false);
    },
    onError: () => {
      toast.error("Konnte Key nicht anlegen");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>+ Neuer Key</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Neuen API-Key anlegen</DialogTitle>
          <DialogDescription>
            Wähle ein Label, um diesen Key später zu identifizieren (z. B.
            „Production“, „Recruits“).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="label">Label</Label>
          <Input
            id="label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="z. B. Production"
            maxLength={64}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={label.trim().length === 0 || mutation.isPending}
          >
            {mutation.isPending ? "Erstelle…" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
